import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import classNames from 'classnames';

import DayPickerInput from 'react-day-picker/DayPickerInput';
import {formatDate, parseDate} from 'react-day-picker/moment';

import {
  BottomRightCustomOverlay,
  BottomLeftFixedCustomOverlay,
  TopLeftFixedCustomOverlay
} from 'components/DatePicker';

import {
  getDateOrUndefined,
  formatDateOrEmptyString,
  isDateValid,
  DATE_FORMAT,
  getToday
} from 'utils/datetime';
import TailRight from './assets/tail-right.svg';
import clearIcon from './assets/remove-icon.svg';
import YearMonthSelect from './components/YearMonthSelect';

import 'react-day-picker/lib/style.css';
import './styles.css';

class DateRangePicker extends React.Component<any, any> {
  state = {
    start: getDateOrUndefined(this.props.start),
    end: getDateOrUndefined(this.props.end),
    today: getDateOrUndefined(getToday())
  };
  timeout: any = null;
  end: any = null;

  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  focusEnd() {
    // Focus end `end` field. A timeout is required here because the overlays
    // already set timeouts end work well with input fields
    this.timeout = setTimeout(() => this.end.getInput().focus(), 0);
  }

  showStartMonth() {
    const {start, end} = this.state;
    if (!start) {
      return;
    }
    if (moment(end).diff(moment(start), 'months') < 2) {
      const daypicker = this.end.getDayPicker();
      if (daypicker) {
        daypicker.showMonth(start);
      }
    }
  }

  changeStart = value => {
    if (isDateValid(value)) {
      this.setState({...this.state, start: value, end: this.state.end}, () =>
        this.handleStartChange(value)
      );
    }
  };

  changeEnd = value => {
    if (isDateValid(value)) {
      this.setState({...this.state, start: this.state.start, end: value}, () =>
        this.handleEndChange(value)
      );
    }
  };

  handleBothDatesChange = (fromDate, toDate) => {
    this.props.handleBothDatesChange &&
      this.props.handleBothDatesChange(fromDate, toDate);
  };

  handleEndChange = value => {
    if (this.props.handleChange) {
      this.props.handleChange('endDate', formatDateOrEmptyString(value));
    }
    this.showStartMonth();
    if (this.state.start && value !== '') {
      this.handleBothDatesChange(this.state.start, value);
    }
    if (value === '' && this.props.handleBothDatesClear) {
      clearTimeout(this.timeout);
      this.props.handleBothDatesClear();
    }
  };

  handleStartChange = value => {
    if (this.props.handleChange) {
      this.props.handleChange('startDate', formatDateOrEmptyString(value));
    }
    if (!this.state.end) {
      this.focusEnd();
    }
    if (this.state.end && value !== '') {
      this.handleBothDatesChange(value, this.state.end);
    }
    if (value === '' && this.props.handleBothDatesClear) {
      clearTimeout(this.timeout);
      this.props.handleBothDatesClear();
    }
  };

  clearEndDate = () => {
    this.setState({...this.props, start: this.state.start, end: ''}, () =>
      this.handleEndChange('')
    );
  };

  clearStartDate = () => {
    this.setState({...this.state, start: '', end: this.state.end}, () =>
      this.handleStartChange('')
    );
  };

  getCaptionElement = handleChange => {
    return ({date, localeUtils}) => (
      <YearMonthSelect
        date={date}
        today={this.state.today}
        localeUtils={localeUtils}
        onChange={handleChange}
      />
    );
  };

  componentDidUpdate(prevProps) {
    if (
      this.props.start !== prevProps.start ||
      this.props.end !== prevProps.end
    ) {
      this.setState({
        ...this.state,
        start: getDateOrUndefined(this.props.start),
        end: getDateOrUndefined(this.props.end)
      });
    }
  }

  render() {
    const {
      placeholderEnd,
      placeholderStart,
      disableBeforeToday,
      className,
      dark,
      // If we pass overlayComponent as undefined - the default overlay will be used
      overlayComponent = undefined,
      disabled,
      selectYearAndMonth
    } = this.props;
    const {start, end, today} = this.state;
    const modifiers = {start, end};

    const dayPicker1CaptionElement = this.getCaptionElement(this.changeStart);

    const dayPickerProps1: any = {
      selectedDays: [start, {from: start, to: end}],
      disabledDays: {
        after: end,
        before: disableBeforeToday ? today : ''
      },
      modifiers,
      numberOfMonths: 2,
      captionElement: selectYearAndMonth ? dayPicker1CaptionElement : undefined
    };

    const dayPicker2CaptionElement = this.getCaptionElement(this.changeEnd);

    const dayPickerProps2: any = {
      selectedDays: [start, {from: start, to: end}],
      disabledDays: {before: start},
      modifiers,
      month: start,
      numberOfMonths: 2,
      captionElement: selectYearAndMonth ? dayPicker2CaptionElement : undefined
    };

    return (
      <div
        className={classNames('DateRangePicker', className, {dark, disabled})}>
        <div className="dateContainer">
          <DayPickerInput
            overlayComponent={overlayComponent}
            value={start}
            placeholder={placeholderStart || DATE_FORMAT}
            format={DATE_FORMAT}
            formatDate={formatDate}
            parseDate={parseDate}
            dayPickerProps={dayPickerProps1}
            onDayChange={this.changeStart}
            inputProps={{disabled}}
          />
          {start && (
            <img
              src={clearIcon}
              className="clearIcon start"
              alt="Clear date"
              onClick={this.clearStartDate}
            />
          )}
        </div>
        <div className="arrowContainer">
          <img src={TailRight} alt="arrow" />
        </div>
        <div className="dateContainer">
          <DayPickerInput
            overlayComponent={overlayComponent}
            ref={el => (this.end = el)}
            value={end}
            placeholder={placeholderEnd || DATE_FORMAT}
            format={DATE_FORMAT}
            formatDate={formatDate}
            parseDate={parseDate}
            dayPickerProps={dayPickerProps2}
            onDayChange={this.changeEnd}
            inputProps={{disabled}}
          />
          {end && (
            <img
              src={clearIcon}
              className="clearIcon end"
              alt="Clear date"
              onClick={this.clearEndDate}
            />
          )}
        </div>
      </div>
    );
  }
}

export default DateRangePicker;

export {
  BottomRightCustomOverlay,
  BottomLeftFixedCustomOverlay,
  TopLeftFixedCustomOverlay
};
